import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

/**
 * CanvasPopover is a specialized popover component designed for use within ReactFlow canvas.
 * It forces the popover to render at document.body with a high z-index to escape ReactFlow's
 * stacking contexts and ensure it always appears above nodes.
 *
 * Use this component instead of the standard Popover when rendering popovers within ReactFlow nodes.
 */
function CanvasPopover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot='canvas-popover' {...props} />;
}

function CanvasPopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return (
    <PopoverPrimitive.Trigger data-slot='canvas-popover-trigger' {...props} />
  );
}

function CanvasPopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  // Force portal to document.body to escape ReactFlow's stacking context
  const portalContainer =
    typeof document !== 'undefined' ? document.body : undefined;

  return (
    <PopoverPrimitive.Portal container={portalContainer}>
      <PopoverPrimitive.Content
        data-slot='canvas-popover-content'
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[9999] w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function CanvasPopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return (
    <PopoverPrimitive.Anchor data-slot='canvas-popover-anchor' {...props} />
  );
}

export {
  CanvasPopover,
  CanvasPopoverTrigger,
  CanvasPopoverContent,
  CanvasPopoverAnchor,
};
